import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AppearanceContext,
  type AppearanceContextValue,
  type Density,
  type ResolvedTheme,
  type ThemePreference,
} from './appearance-context'

const THEME_KEY = 'cse-theme'
const DENSITY_KEY = 'cse-density'
const DARK_QUERY = '(prefers-color-scheme: dark)'
const DEFAULT_DENSITY: Density = 'comfy'

function isThemePreference(value: string | null): value is ThemePreference {
  return value === 'auto' || value === 'light' || value === 'dark'
}

function isDensity(value: string | null): value is Density {
  return value === 'compact' || value === 'default' || value === 'comfy'
}

/** Product default is light (DESIGN V3). `auto` remains available via ThemeToggle. */
const DEFAULT_THEME: ThemePreference = 'light'

function readTheme(): ThemePreference {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const stored = window.localStorage.getItem(THEME_KEY)
  if (stored === 'auto') {
    window.localStorage.setItem(THEME_KEY, 'light')
    return 'light'
  }
  return isThemePreference(stored) ? stored : DEFAULT_THEME
}

function readDensity(): Density {
  if (typeof window === 'undefined') return DEFAULT_DENSITY
  const stored = window.localStorage.getItem(DENSITY_KEY)
  return isDensity(stored) ? stored : DEFAULT_DENSITY
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches
}

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'auto') return systemPrefersDark() ? 'dark' : 'light'
  return pref
}

// Owner unique de `<meta name="theme-color">` : valeurs dark/light déterministes
// (le branding ne l'écrase plus - cf. BrandCssVariables). Crée le meta s'il manque.
function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', resolved)
  document.documentElement.style.colorScheme = resolved
  let meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'theme-color')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', resolved === 'dark' ? '#0f1419' : '#f7fafa')
}

function applyDensity(density: Density): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-d', density)
}

/**
 * Source de vérité unique pour le thème (auto/clair/sombre) et la densité
 * (compact/default/comfy). Persiste en localStorage et applique sur
 * `<html data-theme>` / `<html data-d>`. En mode `auto`, suit `prefers-color-scheme`.
 */
export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(readTheme)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(readTheme()))
  const [density, setDensityState] = useState<Density>(readDensity)

  // Effets DOM uniquement (systèmes externes) - pas de setState ici.
  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  useEffect(() => {
    applyDensity(density)
  }, [density])

  // En mode auto, suit les changements système (setState dans un handler externe).
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined') return
    const media = window.matchMedia(DARK_QUERY)
    const onChange = () => setResolvedTheme(systemPrefersDark() ? 'dark' : 'light')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next)
    setResolvedTheme(resolveTheme(next))
    if (typeof window !== 'undefined') window.localStorage.setItem(THEME_KEY, next)
  }, [])

  const setDensity = useCallback((next: Density) => {
    setDensityState(next)
    if (typeof window !== 'undefined') window.localStorage.setItem(DENSITY_KEY, next)
  }, [])

  const value = useMemo<AppearanceContextValue>(
    () => ({ theme, resolvedTheme, setTheme, density, setDensity }),
    [theme, resolvedTheme, setTheme, density, setDensity],
  )

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>
}
